import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    // El details abierto más cercano: con el foco en el summary de un submenú ya cerrado,
    // closest('details') devolvía ese submenú y el segundo Escape no hacía nada (R174).
    const target = event.target instanceof Element ? event.target.closest('details[open]') : null;
    this.#close(/** @type {HTMLDetailsElement} */ (target ?? this.refs.details));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Paneles de acabados del cajón: se pintan al abrir su submenú (header-drawer.liquid, W27 lote V).
    const diferido = details.querySelector(':scope > .menu-drawer__submenu template[data-tt-diferido]');
    if (diferido instanceof HTMLTemplateElement) diferido.replaceWith(diferido.content);
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
        this.#focusFirst(details);
      }, 0);
    });
  }

  /**
   * Lleva el foco dentro del panel recién abierto (R174): al primer elemento del menú en el cajón y al
   * botón «atrás», que dice en qué submenú se está, en un submenú. Los elementos del menú entran con
   * una animación que los deja ocultos durante su retraso; si el foco no entra, se reintenta al acabar.
   * @param {HTMLDetailsElement} details
   */
  #focusFirst(details) {
    const target =
      details === this.refs.details
        ? details.querySelector('.menu-drawer__navigation .menu-drawer__menu-item')
        : details.querySelector('.menu-drawer__back-button');
    if (!(target instanceof HTMLElement)) return;

    target.focus({ preventScroll: true });
    if (document.activeElement !== target) {
      onAnimationEnd(target, () => {
        if (details.classList.contains('menu-open')) target.focus({ preventScroll: true });
      });
    }
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        // El foco vuelve al botón de menú, no a body (R031).
        summary.focus({ preventScroll: true });
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
          // Y al volver de un submenú, a su entrada en el cajón.
          summary.focus({ preventScroll: true });
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
